import { useState, useCallback, useEffect } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";

interface Template {
  id: number;
  fileName: string;
  fileSize: number;
  templateType: string;
  url: string;
}

interface ApiError {
  message: string;
  status?: number;
}

export const useDocumentTemplates = (
  documentTypeId?: number,
  workspace_id?: string,
) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const authFetch = useAuthFetch();

  const fetchTemplates = useCallback(async () => {
    if (!documentTypeId || !workspace_id) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/templates?workspace_id=${workspace_id}`,
        "fetch templates",
      );
      setTemplates(response);
    } catch (err) {
      const error = err as any;
      setError({
        message: error.message || "Failed to load templates",
        status: error.status,
      });
      console.error("Template fetch error:", error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
  }, [documentTypeId, workspace_id]);

  const deleteTemplate = useCallback(
    async (templateId: number) => {
      if (!documentTypeId || !workspace_id) return;

      setError(null);
      setDeletingId(templateId);
      try {
        await authFetch(
          `/api/workspace/meridian/admin/document_types/${documentTypeId}/templates/${templateId}?workspace_id=${workspace_id}`,
          "delete template",
          {
            method: "DELETE",
          },
        );
        setTemplates((prev) =>
          prev.filter((template) => template.id !== templateId),
        );
      } catch (err) {
        const error = err as any;
        setError({
          message: error.message || "Failed to delete template",
          status: error.status,
        });
        console.error("Template delete error:", error);
        throw error; // Propagate error to FileUploader
      } finally {
        setDeletingId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
    [documentTypeId, workspace_id],
  );

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    isLoading,
    error,
    refreshTemplates: fetchTemplates,
    deleteTemplate,
    deletingId,
  };
};
